import GetSizeClassV2 from '@omni/kit/contexts/SizeClassContext/GetSizeClassV2';
import { SizeClassV2 } from '@omni/kit/contexts/SizeClassContext/types';
import AccountsService from '@omni/kit/services/AccountsService';
import { PlayFormat } from '@omni/kit/services/AnalyticsService/Types';
import Colors from '@omni/kit/theming/Colors';
import FontSize from '@omni/kit/theming/FontSize';
import Spacing from '@omni/kit/theming/Spacing';
import { transparentHexColor } from '@omni/kit/utilities/utilities';
import moment from 'moment';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LayoutChangeEvent, StyleSheet, View } from 'react-native';

import KitSafeAreaProvider from '../KitSafeaAreaProvider';
import LiveCountdown from '../LiveCountdown';
import {
  KitPlayerProps,
  PlayerContainerDimensionsProps,
} from './KitPlayer.types';
import { KitPlayerRef, useSetupKitPlayerRefHandle } from './KitPlayerRef';
import PlayerControls, {
  PlayerControlsRef,
  PlayerTimelineRef,
} from './components/Controls';
import { ActionIndicatorRef } from './components/Controls/components/ActionIndicator';
import FullscreenWrapper, {
  FullscreenWrapperRef,
} from './components/FullscreenWrapper';
import PlayerAudioArtwork from './components/PlayerAudioArtwork';
import { PlayerPromptRef } from './components/PlayerPrompt';
import PlayerVideo from './components/PlayerVideo/PlayerVideo';
import {
  PlayerVideoProps,
  PlayerVideoRef,
} from './components/PlayerVideo/PlayerVideo.types';
import PrePlayPoster from './components/PrePlayPoster';
import usePlayerVideoEvents from './hooks/usePlayerVideoEvents';
import useSegmentLog from './hooks/useSegmentLog';
import { PlayerEvents } from './playbackTypes';
import { MEDIA_TYPE, TogglePlayFunction } from './types';
import { sendPlayEvent } from './utils/analytics';
import checkForCanPlayEventBug from './utils/checkForCanPlayEventBug';
import { getPlayId } from './utils/playId';

const appDebug = require('debug')('tca:KitPlayer');

const KitPlayer = React.forwardRef<KitPlayerRef, KitPlayerProps>(
  (
    {
      autoPlay = false,
      branding,
      controlOverrides = {},
      controlType = 'default',
      data,
      isFullHeight = false,
      isLive = false,
      onDismiss,
      onFullscreen,
      style = {},
      featureInfo = false,
      featureGiving = false,
      featureShareable = false,
      logoWatermark,
      mediaType = 'video',
      mute = false,
      onPause,
      onPlay,
      onPlayOverride,
      onProgress,
      onSeekEnd,
      prePlayPoster,
      previousTimestamp,
      seekToTimestamp,
    },
    kitPlayerRef
  ) => {
    const containerRef = useRef<View>(null);
    const fullscreenRef = useRef<FullscreenWrapperRef>(null);
    const playerVideoRef = useRef<PlayerVideoRef>(null);
    const playerControlsRef = useRef<PlayerControlsRef>(null);
    const playerTimelineRef = useRef<PlayerTimelineRef>(null);
    const actionIndicatorRef = useRef<ActionIndicatorRef>(null);
    const playerPromptRef = useRef<PlayerPromptRef>(null);
    const bufferTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [playerContainerDimensions, setPlayerContainerDimensions] =
      useState<PlayerContainerDimensionsProps>({
        width: 0,
        height: 0,
      });
    const [playId, setPlayId] = useState<string>();
    const [isSeeking, setIsSeeking] = useState(false);
    const [showPrePlayPoster, setShowPrePlayPoster] = useState(!autoPlay);
    const [hasGivingEnabled, setHasGivingEnabled] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [currentMediaType, setCurrentMediaType] =
      useState<MEDIA_TYPE>(mediaType);
    const [detectedLive, setDetectedLive] = useState(false);

    const shouldBeLive = useMemo(() => {
      return isLive || detectedLive;
    }, [isLive, detectedLive]);

    const checkifCountingDown = useCallback(() => {
      if (!data?.broadcastStartAt) return false;

      const now = moment.utc();
      const startAt = moment.utc(data?.broadcastStartAt);

      const hasBroadcastStarted = now.diff(startAt, 'seconds') >= 0;

      return !hasBroadcastStarted;
    }, [data?.broadcastStartAt]);

    const [wasCountingDown] = useState(checkifCountingDown());
    const [isStillCountingDown, setIsStillCountingDown] = useState(
      checkifCountingDown()
    );
    const { width: playerContainerWidth } = playerContainerDimensions;

    const sizeClass = GetSizeClassV2(playerContainerWidth);
    const isSmallScreen = sizeClass <= SizeClassV2.XS;

    const currentMediaUrl = useMemo(() => {
      if (typeof data.url === 'object') {
        return currentMediaType === 'video' ? data.url.video : data.url.audio;
      }

      return data.url;
    }, [data.url, currentMediaType]);

    /**
     * If the url is a string, then the media type is determined by the
     * currentMediaType. Otherwise, we assume the url is an object and should
     * contain both video and audio urls which is reinforced by the type system
     */
    const hasAudio = useMemo(() => {
      return typeof data.url === 'string' ? currentMediaType === 'audio' : true;
    }, [data.url, currentMediaType]);

    const hasVideo = useMemo(() => {
      return typeof data.url === 'string' ? currentMediaType === 'video' : true;
    }, [data.url, currentMediaType]);

    const analyticsFormat = useMemo<PlayFormat>(
      () => (shouldBeLive ? `live_${currentMediaType}` : currentMediaType),
      [shouldBeLive, currentMediaType]
    );

    const playerVideoEvents = usePlayerVideoEvents({
      analyticsFormat,
      appKey: data.appKey,
      autoPlay,
      currentMediaType,
      isLive: shouldBeLive,
      isSeeking,
      mediaItemShortCode: data.mediaItemShortCode,
      playId,
      url: currentMediaUrl,

      onPause,
      onPlay,
      onProgress,
      playerVideoRef,
    });

    const {
      bufferedTimeSeconds,
      canPlay,
      hasEnded,
      isLoading,
      isPlayerReady,
      isPlaying,
      setIsLoading,
      setIsPlaying,
      totalDurationSeconds,
    } = playerVideoEvents;

    const hasCanPlayEventBug = useMemo(
      () => checkForCanPlayEventBug(currentMediaType),
      [currentMediaType]
    );

    const showLiveCountdown = useMemo(() => {
      if (!data) return false;

      if (!data.broadcastStartAt || !data.broadcastStatus) return false;

      if (isStillCountingDown) return true;

      if (data.broadcastStatus === 'scheduled') {
        return true;
      } else if (data.broadcastStatus === 'live') {
        return wasCountingDown && !canPlay && !hasCanPlayEventBug;
      } else {
        return false;
      }
    }, [
      data,
      isStillCountingDown,
      wasCountingDown,
      canPlay,
      hasCanPlayEventBug,
    ]);

    useSegmentLog({
      appKey: data.appKey,
      currentMediaType,
      disabled: showPrePlayPoster,
      isLive: shouldBeLive,
      isSeeking,
      playerVideoRef,
      playFormat: analyticsFormat,
      playId,
    });

    const sendAnalyticsPlayEvent = useCallback(
      (event: PlayerEvents) => {
        if (!event || !playId || !data.appKey) return;

        sendPlayEvent({
          appKey: data.appKey,
          position: playerVideoRef?.current?.getCurrentTimeInSeconds(),
          event,
          format: analyticsFormat,
          playId,
        });
      },
      [analyticsFormat, data.appKey, playId]
    );

    const fetchGivePermission = useCallback(async () => {
      if (!data.appKey) {
        appDebug('No app key to fetch give permission');

        return;
      }

      try {
        const globalConfigs = await AccountsService.getAll('global-configs', [
          ['filter[app_key]', data.appKey as string],
        ]);

        if (globalConfigs.data) {
          const showGiveButtonBool =
            globalConfigs.data['global-configs'][0]
              ?.show_give_button_on_media_items;
          setHasGivingEnabled(showGiveButtonBool);
        }
      } catch (e) {
        appDebug('Failed to fetch give permission', e);
      }
    }, [data.appKey]);

    const togglePlay: TogglePlayFunction = useCallback(
      ({ forceTo, showAction = true } = {}) => {
        if (!currentMediaUrl) {
          appDebug('No media url to play');

          return;
        }

        if (showPrePlayPoster) {
          if (showLiveCountdown) return;

          if (onPlayOverride) return onPlayOverride();

          sendAnalyticsPlayEvent(PlayerEvents.startMedia);

          setShowPrePlayPoster(false);
        }

        const willPlay = forceTo !== undefined ? forceTo : !isPlaying;

        // If the player is loadig and the user tries to pause, immediately stop loading
        if (!willPlay) {
          setIsLoading(false);
        }

        if (hasEnded && willPlay) {
          sendAnalyticsPlayEvent(PlayerEvents.replay);
        }

        setIsPlaying(willPlay);

        if (showAction) {
          actionIndicatorRef.current?.showAction(willPlay ? 'play' : 'pause');
        }

        /**
         * Added controlOverrides.legacyLiveChat conditional only for LiveChat
         * until controls are flushed out for all use cases
         */
        if (
          shouldBeLive &&
          willPlay &&
          playerVideoRef.current &&
          controlOverrides.legacyLiveChat
        ) {
          playerVideoRef.current.seekToLatestBuffer();
        }
      },
      [
        currentMediaUrl,
        showPrePlayPoster,
        isPlaying,
        hasEnded,
        setIsPlaying,
        shouldBeLive,
        controlOverrides.legacyLiveChat,
        showLiveCountdown,
        onPlayOverride,
        sendAnalyticsPlayEvent,
        setIsLoading,
      ]
    );

    const handleFullscreen = useCallback(
      (isFullscreen: boolean) => {
        sendAnalyticsPlayEvent(
          isFullscreen
            ? PlayerEvents.fullscreenEnter
            : PlayerEvents.fullscreenExit
        );
        onFullscreen?.(isFullscreen);
      },
      [onFullscreen, sendAnalyticsPlayEvent]
    );

    const handleSeeking = useCallback(() => {
      appDebug('handleSeeking');
      setIsSeeking(true);
    }, []);

    const handleSeekStart = useCallback(() => {
      appDebug('handleSeekStart');
      handleSeeking();
    }, [handleSeeking]);

    const handleSeeked = useCallback(() => {
      appDebug('handleSeeked');
      setIsSeeking(false);
      onSeekEnd?.();
      sendAnalyticsPlayEvent(PlayerEvents.seeked);
    }, [onSeekEnd, sendAnalyticsPlayEvent]);

    /** Takes a number in seconds or a function that takes the time in seconds */
    const handleSeekTo = useCallback(
      (updater: number | ((t: number) => number)) => {
        appDebug('handleSeekTo');

        if (bufferTimeoutRef.current) {
          clearTimeout(bufferTimeoutRef.current);
          bufferTimeoutRef.current = null;
        }

        const updaterFunc =
          typeof updater === 'function' ? updater : () => updater;

        setIsLoading(true);

        const currentTimeInSeconds =
          playerVideoRef.current?.getCurrentTimeInSeconds() ?? -1;

        const seekTime = updaterFunc(currentTimeInSeconds);
        playerVideoRef.current?.seekTo(seekTime);
        playerTimelineRef.current?.updateTime();
        playerPromptRef?.current?.update();

        let shouldContinueChecking = true;
        // Set a safety timeout to prevent infinite loading state
        bufferTimeoutRef.current = setTimeout(() => {
          shouldContinueChecking = false;
          setIsLoading(false);
        }, 5000);

        const checkBuffered = () => {
          // Stop checking if we shouldn't continue or player ref is gone
          if (!shouldContinueChecking || !playerVideoRef.current) return;

          const buffered = playerVideoRef.current.getSecondsLoaded();

          if (buffered !== null && buffered >= seekTime) {
            // Buffer has reached seek time, stop loading and checking
            shouldContinueChecking = false;
            setIsLoading(false);

            if (bufferTimeoutRef.current) {
              clearTimeout(bufferTimeoutRef.current);
              bufferTimeoutRef.current = null;
            }
          } else if (shouldContinueChecking) {
            setTimeout(checkBuffered, 200);
          }
        };

        checkBuffered();
      },
      [setIsLoading]
    );

    const handleLayout = useCallback(
      (event: LayoutChangeEvent) => {
        const { width, height } = event.nativeEvent.layout;
        setPlayerContainerDimensions({ width, height });
      },
      [setPlayerContainerDimensions]
    );

    const handleFastSeekStart = useCallback(() => {
      togglePlay({ forceTo: false, showAction: false });
    }, [togglePlay]);

    const handleFastSeekEnd = useCallback(() => {
      appDebug('handleFastSeekEnd');
      setIsLoading(true);
      togglePlay({ forceTo: true, showAction: false });
    }, [togglePlay, setIsLoading]);

    const onChangeMediaType = useCallback(
      (mediaType: MEDIA_TYPE) => {
        sendAnalyticsPlayEvent(
          mediaType === 'video'
            ? PlayerEvents.switchToVideo
            : PlayerEvents.switchToAudio
        );

        setCurrentMediaType(mediaType);
      },
      [sendAnalyticsPlayEvent]
    );

    useEffect(() => {
      setCurrentMediaType(mediaType);
    }, [data.url, mediaType]);

    useEffect(() => {
      getPlayId((uuid: string) => setPlayId(uuid ?? 'unknown'));
    }, []);

    useEffect(() => {
      if (seekToTimestamp) {
        handleSeekStart();
        handleSeekTo(seekToTimestamp);
      }
    }, [handleSeekTo, seekToTimestamp, handleSeekStart]);

    useEffect(() => {
      fetchGivePermission();
    }, [fetchGivePermission]);

    useSetupKitPlayerRefHandle(kitPlayerRef, {
      playerVideoRef,
      playerControlsRef,
      fullscreenRef,
      isPlaying,
      totalDurationSeconds: totalDurationSeconds ?? -1,
      togglePlay,
      isPrePlayPosterVisible: showPrePlayPoster,
    });

    return (
      <KitSafeAreaProvider>
        <FullscreenWrapper
          ref={fullscreenRef}
          playerVideoRef={playerVideoRef}
          playerControlsRef={playerControlsRef}
          onFullscreenChange={handleFullscreen}
          shouldStretchFullscreen={isFullHeight}
        >
          {({ isFullscreen, toggleFullscreen }) => (
            <View
              ref={containerRef}
              id='kit-player-container'
              testID='kit-player-container'
              style={[
                styles.container,
                isFullHeight && { height: '100%' },
                style,
              ]}
              onLayout={handleLayout}
            >
              <PlayerVideo
                containerRef={containerRef}
                extraSource={data.extraSource}
                mediaType={currentMediaType}
                muted={mute}
                onBufferEnd={playerVideoEvents.onVideoBufferEnd}
                onBufferStart={playerVideoEvents.onVideoBufferStart}
                onDurationChange={playerVideoEvents.onDurationChange}
                onCanplay={playerVideoEvents.onVideoCanplay}
                onEnd={playerVideoEvents.onVideoEnd}
                onError={playerVideoEvents.onVideoError}
                onLoadStart={playerVideoEvents.onVideoLoadStart}
                onPause={playerVideoEvents.onVideoPause}
                onPlay={playerVideoEvents.onVideoPlay}
                onProgress={playerVideoEvents.onVideoProgress}
                onReady={playerVideoEvents.onVideoReady}
                playbackRate={playbackRate}
                playing={isPlaying}
                ref={playerVideoRef}
                uri={currentMediaUrl}
                controlOverrides={controlOverrides}
                isLive={shouldBeLive}
                onLiveDetected={setDetectedLive}
                onSeeked={handleSeeked}
                onSeeking={handleSeeking}
              />

              {currentMediaType === 'audio' ? (
                <PlayerAudioArtwork
                  url={data.audioArtwork?.url}
                  color={data.audioArtwork?.color}
                  playerContainerDimensions={playerContainerDimensions}
                />
              ) : null}

              <PrePlayPoster
                {...prePlayPoster}
                show={showPrePlayPoster}
                branding={branding}
                containerSize={sizeClass}
                title={featureInfo ? data.title : ''}
                subtitle={featureInfo && !isSmallScreen ? data.subtitle : ''}
                isLive={Boolean(!showLiveCountdown && shouldBeLive)}
              />

              {showLiveCountdown ? (
                <View style={styles.liveCountdown}>
                  <LiveCountdown
                    broadcastStartTime={data.broadcastStartAt || ''}
                    broadcastStatus={data.broadcastStatus || 'scheduled'}
                    style={{
                      paddingHorizontal: Spacing.l,
                      paddingBottom: Spacing.xl,
                      paddingTop: Spacing.xl,
                      backgroundColor: transparentHexColor(Colors.N1000, 90.2),
                    }}
                    liveOnTextStyle={{
                      marginBottom: Spacing.m,
                    }}
                    hideOnEnd={!showLiveCountdown}
                    showLiveOnDate={true}
                    showLiveStreamBeginShortly={true}
                    colonStyle={{
                      marginLeft: 2,
                      marginRight: 2,
                      opacity: 0.75,
                    }}
                    numberFontSize={FontSize.xxl}
                    labelFontSize={FontSize.s}
                    onCountdownEnd={() => {
                      setIsStillCountingDown(false);
                    }}
                  />
                </View>
              ) : (
                <PlayerControls
                  playId={playId}
                  analyticsFormat={analyticsFormat}
                  actionIndicatorRef={actionIndicatorRef}
                  bufferedTimeSeconds={bufferedTimeSeconds}
                  containerRef={containerRef}
                  playerContainerDimensions={playerContainerDimensions}
                  controlOverrides={
                    showPrePlayPoster
                      ? {
                          skipBackward: !showPrePlayPoster,
                          skipForward: !showPrePlayPoster,
                        }
                      : {
                          //in case detectedLive is true, we want to disable settings
                          ...controlOverrides,
                          ...(detectedLive && { settings: false }),
                        }
                  }
                  controlType={controlType}
                  data={data}
                  featureGiving={featureGiving ? hasGivingEnabled : false}
                  featureInfo={featureInfo}
                  featureShareable={featureShareable}
                  hasAudio={hasAudio}
                  hasVideo={hasVideo}
                  isFullscreen={isFullscreen}
                  isLive={shouldBeLive}
                  isLoading={isLoading}
                  isPlayerReady={isPlayerReady}
                  isPlaying={isPlaying}
                  isPrePlayPosterShowing={showPrePlayPoster}
                  logoWatermark={logoWatermark}
                  mediaType={currentMediaType}
                  onChangeMediaType={onChangeMediaType}
                  onDismiss={onDismiss}
                  onFastSeekEnd={handleFastSeekEnd}
                  onFastSeekStart={handleFastSeekStart}
                  onPlaybackSpeedChange={setPlaybackRate}
                  onSeekStart={handleSeekStart}
                  onSeekTo={handleSeekTo}
                  onToggleFullscreen={toggleFullscreen}
                  onTogglePlay={togglePlay}
                  playerTimelineRef={playerTimelineRef}
                  playerPromptRef={playerPromptRef}
                  playerVideoRef={playerVideoRef}
                  previousTimestamp={previousTimestamp}
                  ref={playerControlsRef}
                  totalDurationSeconds={totalDurationSeconds}
                  mediaUrl={currentMediaUrl}
                  branding={branding}
                />
              )}
            </View>
          )}
        </FullscreenWrapper>
      </KitSafeAreaProvider>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    backgroundColor: Colors.N1000,
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
  },
  liveCountdown: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    position: 'absolute',
    width: '100%',
  },
});

export default KitPlayer;
